import { useState } from 'react';
import { Loader2, Send } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { usePipelineChat } from '@/hooks/use-pipeline-chat';
import { ProposalDiffModal } from './ProposalDiffModal';
import type { PipelineEdge, PipelineNode } from '../../types/pipeline';
import type { ValidationResult } from '@/lib/validation-types';

interface PipelineBuilderChatPanelProps {
  pipelineId: string | null;
  selectedNodeId?: string | null;
  nodes?: PipelineNode[];
  edges?: PipelineEdge[];
  validationResult?: ValidationResult;
  onProposalApplied?: () => Promise<void> | void;
}

export function PipelineBuilderChatPanel({
  pipelineId,
  selectedNodeId,
  nodes = [],
  edges = [],
  validationResult,
  onProposalApplied,
}: PipelineBuilderChatPanelProps) {
  const [input, setInput] = useState('');
  const [isDiffOpen, setIsDiffOpen] = useState(false);
  const [isSubmittingProposal, setIsSubmittingProposal] = useState(false);

  const {
    mode,
    messages,
    proposal,
    proposals,
    isLoading,
    canSend,
    contextSummary,
    suggestedPrompts,
    setMode,
    setActiveProposal,
    sendMessage,
    applyProposal,
    rejectProposal,
    clearChat,
  } = usePipelineChat({
    pipelineId,
    selectedNodeId,
    nodes,
    edges,
    validationResult,
    onProposalApplied,
  });

  const onSend = async () => {
    if (!input.trim()) return;
    await sendMessage(input);
    setInput('');
  };

  const onApplySelected = async (selectedOperationIndexes: number[]) => {
    setIsSubmittingProposal(true);
    const isApplied = await applyProposal(selectedOperationIndexes);
    setIsSubmittingProposal(false);
    if (isApplied) {
      setIsDiffOpen(false);
    }
  };

  const onRejectProposal = async () => {
    setIsSubmittingProposal(true);
    const isRejected = await rejectProposal();
    setIsSubmittingProposal(false);
    if (isRejected) {
      setIsDiffOpen(false);
    }
  };

  return (
    <section className="rounded-lg border bg-card">
      <div className="border-b p-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold">Pipeline Chat</h3>
            <p className="text-xs text-muted-foreground">
              Enrich pipeline secara iteratif dengan patch terstruktur.
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={clearChat}>
            Reset
          </Button>
        </div>

        <div className="mt-3 flex items-center gap-2">
          <Button
            size="sm"
            variant={mode === 'GLOBAL' ? 'default' : 'outline'}
            onClick={() => setMode('GLOBAL')}
          >
            Global
          </Button>
          <Button
            size="sm"
            variant={mode === 'SELECTION' ? 'default' : 'outline'}
            onClick={() => setMode('SELECTION')}
          >
            Selection
          </Button>
          {mode === 'SELECTION' && (
            <span className="text-xs text-muted-foreground">
              {selectedNodeId ? `Node: ${selectedNodeId}` : 'Select node first'}
            </span>
          )}
        </div>
      </div>

      <div className="border-b bg-muted/20 px-3 py-2">
        <p className="text-[11px] text-muted-foreground">
          Context: {contextSummary.nodeCount} node • {contextSummary.edgeCount} edge • health{' '}
          {contextSummary.healthScore ?? '-'} • selected{' '}
          {contextSummary.selectedNodeLabel || 'none'}
          {contextSummary.selectedNodeType ? ` (${contextSummary.selectedNodeType})` : ''}
        </p>
      </div>

      {suggestedPrompts.length > 0 && (
        <div className="flex flex-wrap gap-2 border-b p-3">
          {suggestedPrompts.map((prompt) => (
            <Button
              key={prompt}
              type="button"
              variant="outline"
              size="sm"
              className="h-auto whitespace-normal py-1 text-left text-xs"
              onClick={() => setInput(prompt)}
            >
              {prompt}
            </Button>
          ))}
        </div>
      )}

      <div className="max-h-72 space-y-2 overflow-y-auto p-3" data-testid="builder-chat-messages">
        {messages.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            Belum ada pesan. Kirim instruksi untuk enrich pipeline.
          </p>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={
                message.role === 'user'
                  ? 'ml-6 rounded-md bg-primary/10 p-2 text-sm'
                  : 'mr-6 rounded-md bg-muted p-2 text-sm'
              }
            >
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                {message.role}
              </p>
              <p>{message.content}</p>
            </div>
          ))
        )}
      </div>

      {proposal && (
        <div className="border-t bg-muted/30 p-3 text-xs">
          <p className="font-medium">
            Proposal variants: {proposals.length || 1}
          </p>
          <div className="mt-2 grid gap-2">
            {(proposals.length > 0 ? proposals : [proposal]).map((candidate) => {
              const isActive = proposal.id === candidate.id;
              return (
                <button
                  key={candidate.id}
                  type="button"
                  className={`rounded-md border px-2 py-2 text-left transition ${
                    isActive
                      ? 'border-primary bg-primary/10'
                      : 'border-border bg-background hover:border-primary/50'
                  }`}
                  onClick={() => setActiveProposal(candidate.id)}
                >
                  <p className="text-xs font-semibold">
                    {candidate.variantLabel || 'PROPOSAL'} • {candidate.operations.length} ops
                  </p>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {candidate.insights?.[0] || 'No summary insight'}
                  </p>
                </button>
              );
            })}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setIsDiffOpen(true)}>
              Review Diff
            </Button>
          </div>
        </div>
      )}

      <div className="border-t p-3">
        <div className="flex gap-2">
          <Textarea
            value={input}
            onChange={(event) => setInput(event.target.value)}
            rows={2}
            className="resize-none"
            placeholder="Enrich pipeline..."
            disabled={isLoading}
          />
          <Button onClick={onSend} disabled={!input.trim() || !canSend} aria-label="Send">
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      <ProposalDiffModal
        proposal={proposal}
        open={isDiffOpen}
        onOpenChange={setIsDiffOpen}
        onApply={onApplySelected}
        onReject={onRejectProposal}
        isSubmitting={isSubmittingProposal}
      />
    </section>
  );
}
